import React, { useCallback, useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import type { PatientFile } from '../../services/cloud-files';
import FileUploadCard from '../upload/FileUploadCard';
import PatientRecapCard from '../PatientRecapCard';
import Spinner from '../Spinner';

interface UploadRecordsModalProps {
    isOpen: boolean;
    patientId: string | null;
    patientName: string;
    statusLabel: string;
    rejectionNote: string | null;
    rejectionEventId: string | null;
    isLoadingNote: boolean;
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

/**
 * Re-upload records after admin rejected the initial submission. Single-step:
 *
 *   PatientRecapCard
 *   ODO Notes (rejection reason)
 *   Add-files drop zone
 *   Footer: [Upload later] [Submit]
 *
 * Submit triggers the parent's onSubmit, which calls ExitHold so the engine
 * resumes the case to RecordsUnderReview (resolved via resumeFromStatus).
 */
const UploadRecordsModal: React.FC<UploadRecordsModalProps> = ({
    isOpen,
    patientId,
    patientName,
    statusLabel,
    rejectionNote,
    rejectionEventId,
    isLoadingNote,
    isSubmitting,
    onClose,
    onSubmit
}) => {
    const { getProfilePictureUrl, getProfileThumbnailUrl } = usePatientMetadata();
    const [files, setFiles] = useState<PatientFile[]>([]);
    const [uploadingCount, setUploadingCount] = useState(0);

    const isUploading = uploadingCount > 0;
    const canSubmit = files.length > 0 && !isUploading && !isSubmitting;

    const handleUploadStateChange = useCallback((_isUploading: boolean, delta: 1 | -1) => {
        setUploadingCount((prev) => Math.max(0, prev + delta));
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setFiles([]);
            setUploadingCount(0);
        }
    }, [isOpen]);

    if (!isOpen || !patientId) return null;

    const profilePictureUrl = getProfilePictureUrl(patientId);
    const profileThumbnailUrl = getProfileThumbnailUrl(patientId);

    // Backdrop click closes the modal, but only while the user isn't
    // mid-action (mirrors the X button's disabled state). `stopPropagation`
    // on the inner panel ensures clicks inside the dialog never bubble up
    // to the backdrop handler.
    const handleBackdropClick = isUploading || isSubmitting ? undefined : onClose;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleBackdropClick}
        >
            <div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4">
                    <h2 className="text-xl font-bold text-gray-800">Upload records</h2>
                    <button
                        onClick={onClose}
                        disabled={isUploading || isSubmitting}
                        className={`${isUploading || isSubmitting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-800'}`}
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content — single step: recap → notes → upload */}
                <div className="px-6 pb-4 space-y-5 overflow-y-auto scrollbar-hide">
                    <PatientRecapCard
                        name={patientName}
                        profilePictureUrl={profilePictureUrl || ''}
                        profileThumbnailUrl={profileThumbnailUrl}
                        treatmentStatus={statusLabel}
                        duration="N/A"
                        currentAligner={0}
                        totalAligners={0}
                    />

                    {/* ODO Notes — rejection reason from the latest reject-records event */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-bold text-gray-800">ODO Notes</h3>
                        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 leading-relaxed break-words min-h-[3.5rem] flex items-center">
                            {isLoadingNote ? (
                                <span className="text-gray-400">Loading notes…</span>
                            ) : (
                                (rejectionNote ?? 'No notes available.')
                            )}
                        </div>
                    </div>

                    <FileUploadCard
                        label="Add files"
                        patientId={patientId}
                        fileType="ADDITIONAL"
                        accept="*"
                        description="Records"
                        timelineEventId={rejectionEventId ?? undefined}
                        onUploadedFilesChange={setFiles}
                        onUploadStateChange={handleUploadStateChange}
                    />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        disabled={isUploading || isSubmitting}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg border ${
                            isUploading || isSubmitting
                                ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                : 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        Upload later
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={!canSubmit}
                        className={`px-4 py-2 text-sm font-bold rounded-lg flex items-center gap-2 ${
                            canSubmit
                                ? 'bg-gray-800 text-white hover:bg-gray-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting && <Spinner />}
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadRecordsModal;
